import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircleIcon, ClockIcon, BanknotesIcon, ClipboardIcon, LinkIcon, UserGroupIcon } from '@heroicons/react/24/outline';
import { Layout } from '@components';
import { getMembers, getOrders, updateMemberPaymentOptions, createOrderPaymentLink } from '@services/admin.service';
import { MemberUser, Order, OrderProductType } from '@utilities/types';

const PAYMENT_STATUS_LABELS: Record<string, { label: string; className: string }> = {
    paid: { label: 'Payée', className: 'bg-emerald-100 text-emerald-700' },
    pending: { label: 'En attente', className: 'bg-amber-100 text-amber-700' },
    deferred: { label: 'Différé', className: 'bg-indigo-100 text-indigo-700' },
    failed: { label: 'Échec', className: 'bg-red-100 text-red-700' },
    cancelled: { label: 'Annulé', className: 'bg-gray-100 text-gray-600' },
};

function formatMoney(amount: number | undefined, currency: string | undefined) {
    if (typeof amount !== 'number' || !Number.isFinite(amount)) return '—';
    const cur = (currency || 'EUR').toUpperCase();
    const symbol = cur === 'EUR' ? '€' : cur;
    return `${amount.toFixed(2)} ${symbol}`;
}

const PRODUCT_LABELS: Record<OrderProductType, string> = {
    suit: 'costume',
    shirt: 'chemise',
    vest: 'gilet',
};

const getProductSummary = (order: Order): string | null => {
    if (order.schemaVersion !== 2 && !order.items?.length && !order.productTypes?.length) return null;

    const counts = (order.items || []).reduce<Record<OrderProductType, number>>(
        (acc, item) => ({
            ...acc,
            [item.productType]: acc[item.productType] + (item.quantity || 1),
        }),
        { suit: 0, shirt: 0, vest: 0 },
    );
    if (!order.items?.length) {
        for (const productType of order.productTypes || []) counts[productType] += 1;
    }

    const summary = (Object.keys(counts) as OrderProductType[])
        .filter((productType) => counts[productType] > 0)
        .map((productType) => `${counts[productType]} ${PRODUCT_LABELS[productType]}${counts[productType] > 1 ? 's' : ''}`)
        .join(' · ');
    return summary || `${order.itemCount || 0} article${order.itemCount === 1 ? '' : 's'}`;
};

export const Payments: FC = () => {
    const [members, setMembers] = useState<MemberUser[]>([]);
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tab, setTab] = useState<'members' | 'orders'>('members');
    const [savingMemberId, setSavingMemberId] = useState<string | null>(null);
    const [generatingId, setGeneratingId] = useState<string | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const fetchAll = useCallback(async () => {
        try {
            setLoading(true);
            const [m, o] = await Promise.all([getMembers(), getOrders()]);
            setMembers(m.data);
            setOrders(o.data);
        } catch (err: any) {
            setError(err.message || 'Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const pendingOrders = useMemo(
        () => orders.filter((o) => o.paymentStatus !== 'paid' && o.paymentStatus !== 'cancelled'),
        [orders],
    );

    const handleToggle = useCallback(async (member: MemberUser, next: boolean) => {
        setSavingMemberId(member.id);
        setError(null);
        try {
            await updateMemberPaymentOptions(member.id, next);
            setMembers((prev) => prev.map((m) => (m.id === member.id ? { ...m, allowDeferredPayment: next } : m)));
        } catch (err: any) {
            setError(err.message || 'Mise à jour impossible');
        } finally {
            setSavingMemberId(null);
        }
    }, []);

    const handleGenerateLink = useCallback(async (orderId: string) => {
        setGeneratingId(orderId);
        setError(null);
        try {
            const res = await createOrderPaymentLink(orderId);
            setOrders((prev) => prev.map((o) => (o.id === orderId ? { ...o, ...res.data } : o)));
        } catch (err: any) {
            setError(err.message || 'Génération du lien impossible');
        } finally {
            setGeneratingId(null);
        }
    }, []);

    const handleCopyLink = useCallback(async (orderId: string, url: string) => {
        try {
            await navigator.clipboard.writeText(url);
            setCopiedId(orderId);
            setTimeout(() => setCopiedId((c) => (c === orderId ? null : c)), 1500);
        } catch {
            // ignore
        }
    }, []);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <span className="loading loading-spinner loading-lg" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="p-6 space-y-6">
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Paiements</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Gérez les options de paiement différé des membres et générez des liens de paiement pour les commandes en attente.
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="px-4 py-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
                        {error}
                    </div>
                )}

                <div className="border-b border-gray-200">
                    <nav className="flex gap-6">
                        <button
                            onClick={() => setTab('members')}
                            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${tab === 'members'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <span className="inline-flex items-center gap-2">
                                <UserGroupIcon className="w-4 h-4" />
                                Membres ({members.length})
                            </span>
                        </button>
                        <button
                            onClick={() => setTab('orders')}
                            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${tab === 'orders'
                                    ? 'border-primary text-primary'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            <span className="inline-flex items-center gap-2">
                                <BanknotesIcon className="w-4 h-4" />
                                Commandes en attente ({pendingOrders.length})
                            </span>
                        </button>
                    </nav>
                </div>

                {tab === 'members' && (
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                                <tr>
                                    <th className="px-4 py-3 text-left">Membre</th>
                                    <th className="px-4 py-3 text-left">Email</th>
                                    <th className="px-4 py-3 text-center">Paiement différé autorisé</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {members.map((m) => {
                                    const allowed = m.allowDeferredPayment === true;
                                    const saving = savingMemberId === m.id;
                                    return (
                                        <tr key={m.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 font-medium text-gray-900">
                                                {m.firstName || m.lastName ? `${m.firstName} ${m.lastName}`.trim() : '—'}
                                            </td>
                                            <td className="px-4 py-3 text-gray-600">{m.email}</td>
                                            <td className="px-4 py-3 text-center">
                                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="toggle toggle-sm toggle-primary"
                                                        checked={allowed}
                                                        disabled={saving}
                                                        onChange={(e) => handleToggle(m, e.target.checked)}
                                                    />
                                                    <span className={`text-xs ${allowed ? 'text-emerald-600' : 'text-gray-400'}`}>
                                                        {allowed ? 'Activé' : 'Désactivé'}
                                                    </span>
                                                </label>
                                            </td>
                                        </tr>
                                    );
                                })}
                                {members.length === 0 && (
                                    <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-400">Aucun membre</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === 'orders' && (
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                                <tr>
                                    <th className="px-4 py-3 text-left">Commande</th>
                                    <th className="px-4 py-3 text-left">Membre / Client</th>
                                    <th className="px-4 py-3 text-right">Montant</th>
                                    <th className="px-4 py-3 text-center">Paiement</th>
                                    <th className="px-4 py-3 text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pendingOrders.map((o) => {
                                    const payConf = PAYMENT_STATUS_LABELS[o.paymentStatus || 'pending'] || PAYMENT_STATUS_LABELS.pending;
                                    const productSummary = getProductSummary(o);
                                    return (
                                        <tr key={o.id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3">
                                                <div className="font-mono text-xs text-gray-500">{o.id.slice(0, 8)}…</div>
                                                <div className="text-xs text-gray-400">{new Date(o.createdAt).toLocaleDateString('fr-FR')}</div>
                                                {productSummary && <div className="mt-0.5 text-xs text-gray-500">{productSummary}</div>}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="text-gray-900">
                                                    {o.member ? `${o.member.firstName} ${o.member.lastName}`.trim() : o.memberEmail}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {o.client ? `→ ${o.client.firstName} ${o.client.lastName}` : ''}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-right font-semibold tabular-nums">
                                                <div>{formatMoney(o.amountTotal, o.currency)}{o.amountSubtotal != null ? ' TTC' : ''}</div>
                                                {o.amountSubtotal != null && (
                                                    <div className="mt-0.5 text-[11px] font-normal text-gray-500">
                                                        {formatMoney(o.amountSubtotal, o.currency)} HT
                                                        {o.vatAmount != null ? ` · TVA ${formatMoney(o.vatAmount, o.currency)}` : ''}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${payConf.className}`}>
                                                    {o.paymentStatus === 'paid' ? <CheckCircleIcon className="w-3 h-3" /> : <ClockIcon className="w-3 h-3" />}
                                                    {payConf.label}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                {o.paymentLinkUrl ? (
                                                    <div className="flex items-center justify-end gap-2">
                                                        <a
                                                            href={o.paymentLinkUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-primary hover:underline"
                                                        >
                                                            <LinkIcon className="w-3.5 h-3.5" />
                                                            Ouvrir
                                                        </a>
                                                        <button
                                                            onClick={() => handleCopyLink(o.id, o.paymentLinkUrl!)}
                                                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg"
                                                        >
                                                            <ClipboardIcon className="w-3.5 h-3.5" />
                                                            {copiedId === o.id ? 'Copié !' : 'Copier'}
                                                        </button>
                                                        <button
                                                            onClick={() => handleGenerateLink(o.id)}
                                                            disabled={generatingId === o.id}
                                                            className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-50"
                                                        >
                                                            Régénérer
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleGenerateLink(o.id)}
                                                        disabled={generatingId === o.id}
                                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-primary text-white rounded-lg hover:bg-primary-focus disabled:opacity-50"
                                                    >
                                                        <LinkIcon className="w-3.5 h-3.5" />
                                                        {generatingId === o.id ? 'Génération…' : 'Générer un lien'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {pendingOrders.length === 0 && (
                                    <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">Aucune commande en attente de paiement</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Layout>
    );
};
